import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import s from './EditSceneModal.module.scss'

import { getIconByMetaverse } from 'constants/MetaverseIcons'

import { useFetch } from 'hooks/useFetch'

import { useAppErrorContext } from 'contexts/AppErrorContext'

import { scenesApi } from 'api/scenes-api'

import { InfoIcon } from 'assets/icons/InfoIcon'
import { TagIcon } from 'assets/icons/TagIcon'
import uploadedIcon from 'assets/svg/success.svg'

import { CustomDropzone } from 'components/Controls/CustomDropzone/CustomDropzone'
import { InputControlled } from 'components/Controls/Input/InputControlled'
import { TextareaControlled } from 'components/Controls/Textarea/TextareaControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'

import { makeSceneSchema } from 'pages/MyScenes/validation'

interface FormType {
  name: string
  description: string
  preview: File[]
  sceneFile: File[]
}

export const EditSceneModal = () => {
  const [searchParams] = useSearchParams()

  const { setAppError } = useAppErrorContext()

  const navigate = useNavigate()
  const { id } = useParams()

  const [thumb, setThumb] = useState('')
  const [sceneLink, setsceneLink] = useState('')
  const [loading, setLoading] = useState(false)

  const {
    handleSubmit,
    control,
    formState: { errors },
    setFocus,
    reset,
    watch,
  } = useForm<FormType>({
    mode: 'onBlur',
    resolver: yupResolver(makeSceneSchema(+(id || 0), thumb, sceneLink)),
    defaultValues: {
      name: '',
      description: '',
      preview: [] as File[],
      sceneFile: [] as File[],
    },
  })

  const sceneFile = watch('sceneFile')

  const onHide = () => {
    navigate(`../?${searchParams}`, { replace: true })
  }

  useEffect(() => {
    setFocus('name')
  }, [])

  //edit reset

  const { data: scene, loading: sceneLoading } = useFetch({
    getData: () => scenesApi.getOne(+(id || 0)),
    fetchIf: !!id,
  })

  useEffect(() => {
    if (id && scene && !loading && !sceneLoading) {
      reset({
        name: scene.name || '',
        description: scene.description || '',
      })
      setsceneLink(scene.scene_file || '')
      setThumb(scene.preview_file || '')
    }
  }, [sceneLoading, loading, id, scene])

  const onSubmit = async (fData: FormType) => {
    if (id) {
      setLoading(true)
      try {
        const formData = new FormData()
        formData.append(
          'data',
          JSON.stringify({
            name: fData.name,
            description: fData.description,
          })
        )
        console.log(fData?.sceneFile[0])
        if (fData?.sceneFile[0]?.name?.includes('.obj')) {
          formData.append(
            'scene_file',
            new Blob(fData?.sceneFile, {
              type: 'model/obj',
            })
          )
        } else {
          formData.append('scene_file', fData?.sceneFile[0])
        }
        formData.append('preview_file', fData?.preview[0])
        const res = await scenesApi.update(+id, formData)
        if (res.status === 200) {
          localStorage.setItem('sceneEdited', 'true')
          navigate(`../?${searchParams}`, { replace: true })
        }
        return
      } catch (e: any) {
        setLoading(false)
        setAppError(e)
        navigate(`../?${searchParams}`, { replace: true })
        return
      }
    }
  }

  return (
    <ModalFullScreen onHide={onHide} loading={loading} className={s.modalWrapper}>
      <div className={s.headerWrapper}>
        <h1 className={s.header}>Edit Scene</h1>
        <p className={s.metaverseInfo}>
          <img src={getIconByMetaverse(scene?.metaverse)} alt="" className={s.metaLogo} />
          <span>for {scene?.metaverse?.name || scene?.metaverse?.engine}</span>
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={s.formWrapper} id="sceneForm">
        <div className={s.formFieldsWrapper}>
          <div className={s.inputsWrapper}>
            <InputControlled
              icon={<TagIcon />}
              name="name"
              placeholder="Enter name..."
              error={errors?.name?.message}
              control={control}
            />
            <TextareaControlled
              icon={<InfoIcon />}
              control={control}
              name="description"
              placeholder="Describe the scene..."
              height={152}
              error={errors?.description?.message}
            />
          </div>
          <div className={s.filesAndCheckboxesWrapper}>
            <div className={s.filesWrapper}>
              <CustomDropzone
                control={control}
                name="preview"
                uploaded={thumb}
                noMediaTitle={<div className={s.dropzoneTitle}>Upload Preview Image in .jpg .png up to 2 MB</div>}
                // @ts-ignore
                error={errors?.preview?.message}
                wrapperStyles={{ maxWidth: 192, width: '100%' }}
                dropzoneStyles={{ height: 128, maxWidth: 192, width: '100%', borderRadius: 8 }}
                iconWidth={40}
                iconHeight={40}
              />
              <CustomDropzone
                control={control}
                name="sceneFile"
                uploaded={
                  scene?.scene_file && !sceneFile?.length ? (
                    <div className={s.sceneUploaded}>
                      <img src={uploadedIcon} alt="uploaded" className={s.sceneUploadedIcon} />
                      <p className={s.sceneUploadedText}>The Scene uploaded</p>
                    </div>
                  ) : sceneFile?.length ? (
                    <div className={s.sceneUploaded}>
                      <img src={uploadedIcon} alt="uploaded" className={s.sceneUploadedIcon} />
                      <p className={s.sceneUploadedText}>The Scene {sceneFile?.[0]?.name} dropped</p>
                    </div>
                  ) : (
                    ''
                  )
                }
                noMediaTitle={<div className={s.dropzoneTitle}>Upload Scene File up to 20 MB</div>}
                // @ts-ignore
                error={errors?.sceneFile?.message}
                wrapperStyles={{ maxWidth: 192, width: '100%' }}
                dropzoneStyles={{ height: 128, maxWidth: 192, width: '100%', borderRadius: 8 }}
                iconWidth={40}
                iconHeight={40}
              />
            </div>
          </div>
        </div>
      </form>
      <div className={s.buttonsWrapper}>
        <CustomButton type="submit" variant="secondary" size="240" form="sceneForm" className={s.saveBtn}>
          Save
        </CustomButton>
        <CustomButton type="button" variant="outlined" onClick={onHide} size="152">
          Close
        </CustomButton>
      </div>
    </ModalFullScreen>
  )
}
